import { useState } from 'react';
import { CheckCircle2, Copy, Plus, Search, Tag, Trash2 } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { Banner } from '../../components/Banner';
import { EmptyState } from '../../components/EmptyState';
import { useResellerCoupons, addResellerCoupon, removeResellerCoupon } from '../../api/reseller';
import { useQueryClient } from '@tanstack/react-query';
import { formatDate } from '../../lib/format';

/** The reseller's own coupons (live from /reseller/coupons). */
export function Coupons() {
  const qc = useQueryClient();
  const { data: resellerCoupons = [] } = useResellerCoupons();
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  async function deleteCoupon(id: string) {
    await removeResellerCoupon(id);
    void qc.invalidateQueries({ queryKey: ['reseller', 'coupons'] });
  }
  const filtered = resellerCoupons.filter(
    (c) =>
      !search ||
      c.code.toLowerCase().includes(search.toLowerCase()) ||
      c.internalName?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Reseller"
        title={`My coupons (${resellerCoupons.length})`}
        subtitle="Discount codes scoped to your customers."
        action={
          <Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setDrawerOpen(true)}>
            New coupon
          </Button>
        }
      />

      <NewCouponDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <Banner tone="info" className="mb-4">
        Only customers signing up under <code className="mono font-medium">acme.itcitysoft.com</code> can redeem these.
        Discounts come out of your margin/commission, not the platform's share.
      </Banner>

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search by code or name"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon={<Tag className="w-5 h-5" />}
            title="No coupons yet"
            description="Create a discount code to share with your customers."
            action={
              <Button size="sm" iconLeft={<Plus className="w-4 h-4" />} onClick={() => setDrawerOpen(true)}>
                New coupon
              </Button>
            }
          />
        ) : (
          <div className="overflow-x-auto nice-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
                <tr>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Code</th>
                  <th className="text-left font-medium px-4 py-3">Type</th>
                  <th className="text-left font-medium px-4 py-3">Amount</th>
                  <th className="text-left font-medium px-4 py-3">Redeemed</th>
                  <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Expires</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="px-5 sm:px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                    <td className="px-5 sm:px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="mono font-medium text-zinc-950 dark:text-zinc-50">{c.code}</span>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard?.writeText(c.code)}
                          className="w-6 h-6 grid place-items-center rounded text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                          title="Copy code"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                      {c.internalName && (
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{c.internalName}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 capitalize text-zinc-700 dark:text-zinc-200">
                      {c.discountType.replace('_', ' ')}
                    </td>
                    <td className="px-4 py-4 text-zinc-700 dark:text-zinc-200">{c.amountLabel}</td>
                    <td className="px-4 py-4 text-zinc-700 dark:text-zinc-200 tabular-nums">
                      {c.redemptionsUsed}
                      {c.redemptionsLimit !== null && (
                        <span className="text-zinc-400 dark:text-zinc-500"> / {c.redemptionsLimit}</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-zinc-600 dark:text-zinc-300 hidden md:table-cell tabular-nums whitespace-nowrap">
                      {c.expiresAt ? formatDate(c.expiresAt, { compact: true }) : '—'}
                    </td>
                    <td className="px-4 py-4">
                      <Badge tone={c.status === 'active' ? 'success' : c.status === 'expired' ? 'neutral' : 'warning'}>
                        {c.status === 'active' ? 'Active' : c.status === 'expired' ? 'Expired' : c.status}
                      </Badge>
                    </td>
                    <td className="px-5 sm:px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => deleteCoupon(c.id)}
                        className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-400 transition"
                        title="Delete coupon"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function NewCouponDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [code, setCode] = useState('');
  const [internalName, setInternalName] = useState('');
  const [discountType, setDiscountType] = useState<'percent' | 'fixed' | 'trial_extension'>('percent');
  const [amount, setAmount] = useState('');
  const [duration, setDuration] = useState<'once' | 'repeating' | 'forever'>('once');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [status, setStatus] = useState<'active' | 'draft'>('active');
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const valid = code.trim().length > 0 && Number(amount) > 0;

  function reset() {
    setCode('');
    setInternalName('');
    setDiscountType('percent');
    setAmount('');
    setDuration('once');
    setMaxRedemptions('');
    setExpiresAt('');
    setStatus('active');
    setSaved(false);
  }

  function close() {
    onClose();
    setTimeout(reset, 250);
  }

  async function save() {
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      const value = discountType === 'fixed' ? Math.round(Number(amount) * 100) : Math.round(Number(amount));
      await addResellerCoupon({
        code: code.trim().toUpperCase(),
        discountType,
        discountValue: value,
        maxRedemptions: maxRedemptions ? Number(maxRedemptions) : null,
        expiresAt: expiresAt || null,
      });
      void qc.invalidateQueries({ queryKey: ['reseller', 'coupons'] });
      setSaved(true);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(
        status === 409
          ? 'You already have a coupon with that code.'
          : status === 400
            ? 'Check the code (2–32 chars) and amount.'
            : 'Could not create the coupon.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  function genCode() {
    const r = Math.random().toString(36).slice(2, 8).toUpperCase();
    setCode(`PROMO-${r}`);
  }

  return (
    <Drawer
      open={open}
      onClose={close}
      title={saved ? 'Coupon created' : 'New coupon'}
      width="lg"
      footer={
        saved ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button onClick={save} loading={submitting} disabled={!valid}>
              Save coupon
            </Button>
          </>
        )
      }
    >
      {saved ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center text-emerald-700 dark:text-emerald-300 mb-4">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
            Coupon <span className="mono">{code}</span> created
          </h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Share the code with customers signing up under your subdomain.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
              Code <span className="text-rose-600">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ACME-EID2026"
                className="flex-1 px-3 py-2.5 rounded-md text-sm bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand transition mono uppercase"
              />
              <Button variant="secondary" size="sm" onClick={genCode} type="button">
                Generate
              </Button>
            </div>
          </div>

          <Input
            label="Internal name"
            placeholder="Eid 2026 promo"
            value={internalName}
            onChange={(e) => setInternalName(e.target.value)}
            hint="Visible to you only — for your records."
          />

          <fieldset>
            <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              Discount type <span className="text-rose-600">*</span>
            </legend>
            <div className="space-y-2">
              <Radio name="dt" label="Percent off" checked={discountType === 'percent'} onChange={() => setDiscountType('percent')} />
              <Radio name="dt" label="Fixed amount off (BDT)" checked={discountType === 'fixed'} onChange={() => setDiscountType('fixed')} />
              <Radio name="dt" label="Trial extension (days)" checked={discountType === 'trial_extension'} onChange={() => setDiscountType('trial_extension')} />
            </div>
          </fieldset>

          <Input
            label={discountType === 'percent' ? 'Percent off' : discountType === 'fixed' ? 'Fixed amount (BDT)' : 'Extra trial days'}
            required
            type="number"
            min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            prefix={
              discountType === 'fixed' ? <span className="text-sm">৳</span> : undefined
            }
            suffix={
              discountType === 'percent' ? <span className="text-sm">%</span> : discountType === 'trial_extension' ? <span className="text-sm">d</span> : undefined
            }
          />

          <fieldset>
            <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Duration</legend>
            <div className="space-y-2">
              <Radio name="dur" label="Once (one invoice only)" checked={duration === 'once'} onChange={() => setDuration('once')} />
              <Radio name="dur" label="Repeating (3 months)" checked={duration === 'repeating'} onChange={() => setDuration('repeating')} />
              <Radio name="dur" label="Forever" checked={duration === 'forever'} onChange={() => setDuration('forever')} />
            </div>
          </fieldset>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Max redemptions"
              type="number"
              min={0}
              value={maxRedemptions}
              onChange={(e) => setMaxRedemptions(e.target.value)}
              placeholder="100"
              hint="Blank = unlimited"
            />
            <Input
              label="Expires at"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              hint="Blank = no expiry"
            />
          </div>

          <fieldset>
            <legend className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Status</legend>
            <div className="space-y-2">
              <Radio name="st" label="Active (live immediately)" checked={status === 'active'} onChange={() => setStatus('active')} />
              <Radio name="st" label="Draft (save without going live)" checked={status === 'draft'} onChange={() => setStatus('draft')} />
            </div>
          </fieldset>

          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Discounts come out of your commission, not the platform's share.
          </p>
        </div>
      )}
    </Drawer>
  );
}

function Radio({ name, label, checked, onChange }: { name: string; label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="text-brand focus:ring-brand" />
      {label}
    </label>
  );
}
